import React, { ReactElement, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as InProgressIcon,
  Error as ErrorIcon,
  Circle as DefaultIcon,
} from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  fetchMotherSessions,
  selectFilteredMotherSessions,
  selectMotherSessionsStatus,
  selectMotherFilters,
  setStatusFilter,
  setSearchFilter,
} from './mother.slice'
import type { MotherSessionStatus } from './types'

dayjs.extend(relativeTime)

type SessionListProps = {
  selectedSession: string | null
  onSessionSelect: (correlationID: string) => void
}

let getStatusIcon = (status: MotherSessionStatus) => {
  switch (status) {
    case 'sessionCompleted':
      return <CheckCircleIcon color="success" fontSize="small" />
    case 'sessionInProgress':
      return <InProgressIcon color="warning" fontSize="small" />
    case 'sessionFailed':
      return <ErrorIcon color="error" fontSize="small" />
    default:
      return <DefaultIcon color="disabled" fontSize="small" />
  }
}

let copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
}

let formatDuration = (seconds: number | null): string => {
  if (!seconds) return ''

  if (seconds < 60) {
    return `${Math.round(seconds)}s`
  }
  if (seconds < 3600) {
    let mins = Math.floor(seconds / 60)
    let secs = Math.round(seconds % 60)
    return `${mins}m ${secs}s`
  }
  let hours = Math.floor(seconds / 3600)
  let mins = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${mins}m`
}

let getSessionDisplayName = (session: any): string => {
  let timestamp = dayjs(session.createdTime).format('MMM D, h:mm A')

  if (session.userEmail) {
    return `${session.userEmail}'s session ${timestamp}`
  }

  // Fallback to user ID + timestamp
  return `User ${session.userID.slice(0, 8)}'s session ${timestamp}`
}

export default ({
  selectedSession,
  onSessionSelect,
}: SessionListProps): ReactElement => {
  let dispatch = useAppDispatch()
  let sessions = useAppSelector(selectFilteredMotherSessions)
  let sessionsStatus = useAppSelector(selectMotherSessionsStatus)
  let filters = useAppSelector(selectMotherFilters)

  // Load sessions on mount
  useEffect(() => {
    if (sessionsStatus === 'uninitialized') {
      dispatch(fetchMotherSessions({}))
    }
  }, [dispatch, sessionsStatus])

  let handleRefresh = () => {
    dispatch(fetchMotherSessions({}))
  }

  let handleStatusFilterChange = (status: string) => {
    dispatch(setStatusFilter(status === 'all' ? null : status))
  }

  let handleSearchChange = (search: string) => {
    dispatch(setSearchFilter(search))
  }

  let sessionStats = useMemo(() => {
    let total = sessions.length
    let completed = sessions.filter(
      (s: any) => s.status === 'sessionCompleted',
    ).length
    let inProgress = sessions.filter(
      (s: any) => s.status === 'sessionInProgress',
    ).length
    let failed = sessions.filter(
      (s: any) => s.status === 'sessionFailed',
    ).length

    return { total, completed, inProgress, failed }
  }, [sessions])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            Sessions
          </Typography>
          <Tooltip title="Refresh sessions">
            <IconButton
              onClick={handleRefresh}
              disabled={sessionsStatus === 'pending'}
              aria-label="Refresh sessions list"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`${sessionStats.completed} Completed`}
            color="success"
            variant="outlined"
            size="small"
            aria-label={`${sessionStats.completed} completed sessions`}
          />
          <Chip
            label={`${sessionStats.inProgress} Active`}
            color="warning"
            variant="outlined"
            size="small"
            aria-label={`${sessionStats.inProgress} active sessions`}
          />
          <Chip
            label={`${sessionStats.failed} Failed`}
            color="error"
            variant="outlined"
            size="small"
            aria-label={`${sessionStats.failed} failed sessions`}
          />
        </Box>

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 2,
          }}
        >
          <TextField
            placeholder="Search sessions..."
            size="small"
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ flex: 1 }}
            aria-label="Search sessions"
            slotProps={{
              input: {
                startAdornment: (
                  <SearchIcon
                    sx={{ color: 'text.secondary', mr: 1 }}
                    aria-hidden="true"
                  />
                ),
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || 'all'}
              label="Status"
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="sessionCompleted">Completed</MenuItem>
              <MenuItem value="sessionInProgress">In Progress</MenuItem>
              <MenuItem value="sessionFailed">Failed</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      {/* Sessions List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {sessionsStatus === 'pending' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {sessionsStatus === 'rejected' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load sessions. Please try again.
          </Alert>
        )}

        {sessionsStatus === 'fulfilled' && sessions.length === 0 && (
          <Box sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
            <Typography>No sessions found</Typography>
          </Box>
        )}

        {sessionsStatus === 'fulfilled' && sessions.length > 0 && (
          <List sx={{ p: 0 }}>
            {sessions.map((session: any) => (
              <ListItem key={session.correlationID} sx={{ p: 0 }}>
                <ListItemButton
                  selected={selectedSession === session.correlationID}
                  onClick={() => onSessionSelect(session.correlationID)}
                  sx={{
                    border: 1,
                    borderColor:
                      selectedSession === session.correlationID
                        ? 'primary.main'
                        : 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {getStatusIcon(session.status)}
                        <Typography variant="body2" component="span">
                          {getSessionDisplayName(session)}
                        </Typography>
                        <Tooltip title="Copy correlation ID">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              copyToClipboard(session.correlationID)
                            }}
                            sx={{ ml: 'auto' }}
                            aria-label={`Copy correlation ID ${session.correlationID}`}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {/* Objective preview */}
                        {session.objective && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.5 }}
                          >
                            {session.objective}
                          </Typography>
                        )}

                        {/* Progress bar */}
                        {session.nodeCount && session.nodeCount > 0 && (
                          <Box sx={{ mb: 0.5 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 0.25,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Progress
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {session.completedNodeCount || 0}/
                                {session.nodeCount} nodes
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={
                                ((session.completedNodeCount || 0) /
                                  session.nodeCount) *
                                100
                              }
                              sx={{ height: 4, borderRadius: 1 }}
                            />
                          </Box>
                        )}

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {dayjs(session.createdTime).fromNow()}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            mt: 0.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          {session.hasExecutionGraph && (
                            <Chip
                              label="Graph"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {session.hasFinalResponse && (
                            <Chip
                              label="Response"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {session.loreCount && session.loreCount > 0 && (
                            <Chip
                              label={`${session.loreCount} Files`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {session.executionDurationSeconds && (
                            <Chip
                              label={formatDuration(
                                session.executionDurationSeconds,
                              )}
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          )}
                          {session.failedNodeCount &&
                            session.failedNodeCount > 0 && (
                              <Chip
                                label={`${session.failedNodeCount} failed`}
                                size="small"
                                variant="outlined"
                                color="error"
                              />
                            )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  )
}
